import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import './LoginPage.css';
import goldenLogo from '@/assets/golden_blue_logo.png';
import labBg from '@/assets/lab-bg.jpg';


const PILLS = ['Lab Registry', 'CAPA Management', 'Certifications', 'Audit Tracking', 'AI Lab Finder'];

export default function LoginPage() {
  const [email,     setEmail    ] = useState('');
  const [password,  setPassword ] = useState('');
  const [showPw,    setShowPw   ] = useState(false);
  const [loading,   setLoading  ] = useState(false);
  const [error,     setError    ] = useState('');
  const { login } = useAuth();
  const navigate   = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message || (err as Error)?.message || '';
      setError(msg || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page" style={{ backgroundImage: `url(${labBg})` }}>
      {/* Dark overlay */}
      <div className="login-overlay" />

      {/* ── LEFT — brand text ───────────────────────────────────── */}
      <div className="login-brand">
        <div className="login-brand-eyebrow">
          <span className="login-brand-dot" />
          <span className="login-brand-eyebrow-text">Lab Management System</span>
        </div>

        <h1 className="login-brand-headline">
          Unified Quality.<br />
          <em>Intelligent Control.</em>
        </h1>

        <p className="login-brand-desc">
          Track, manage and optimise pharmaceutical lab operations with real-time
          CAPA tracking, certification monitoring, and AI-powered partner intelligence.
        </p>

        <div className="login-brand-pills">
          {PILLS.map((p) => (
            <span key={p} className="login-brand-pill">{p}</span>
          ))}
        </div>
      </div>

      {/* ── RIGHT — floating card ────────────────────────────────── */}
      <div className="login-card-wrap">
        <div className="login-card">

          {/* Logo */}
          <div className="login-card-top">
            <img src={goldenLogo} alt="Quantum Kairoz" className="login-card-logo" />
            <div className="login-card-product">
              Quantum <span>Kairoz</span>
            </div>
          </div>

          {/* Form */}
          <div className="login-card-body">
            <div className="login-card-title-row">
              <h2 className="login-card-title">Sign In</h2>
              <div className="login-status">
                <span className="login-status-dot" />
                System Online
              </div>
            </div>

            {error && (
              <div className="login-error">
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="login-field">
                <label className="login-field-label">Email address</label>
                <div className="login-field-wrap">
                  <span className="login-field-icon"><Mail size={14} /></span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="login-input"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-field-label">Password</label>
                <div className="login-field-wrap">
                  <span className="login-field-icon"><Lock size={14} /></span>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="login-input"
                    style={{ paddingRight: 40 }}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-pw-toggle"
                    onClick={() => setShowPw(!showPw)}
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="login-submit">
                {loading && <span className="login-spinner" />}
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>


          </div>

          <div className="login-card-footer">
            Powered by Quantum Kairoz · Forge Quantum Solutions
          </div>
        </div>
      </div>
    </div>
  );
}
